import type { Route } from './+types/policies';
import { buildMeta } from '~/lib/seo';
import { brandingFromMatches, useBranding } from '~/lib/branding';

// The policy BODY is New Leaf's actual legal document: its opt-out address
// (questions@nlcstpete.com), its phone number, Florida governing law, its own
// cancellation fee. Writing another tenant's NAME above that text would produce
// a legal notice that company never agreed to, pointing opt-out requests at a
// different business — the same "Frankenstein record" reasoning that keeps the
// LocalBusiness JSON-LD in root.tsx New-Leaf-only.
//
// So: the document renders for New Leaf. Everyone else gets an honest empty
// state until per-tenant policy content exists. Omission beats substitution,
// and a substituted legal document is the worst case of substitution there is.
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return [
    ...buildMeta({
      title: 'Policies',
      description: `Privacy policy and terms of service for ${branding.name}.`,
      path: '/policies',
      siteName: branding.name,
    }),
    { name: 'robots', content: 'noindex' },
  ];
};

export default function PoliciesPage() {
  const branding = useBranding();

  if (!branding.isNewLeaf) {
    return (
      <main>
        <section className="section-sm">
          <div className="container-narrow">
            <p className="eyebrow page-eyebrow">Legal</p>
            <h1 className="display-md page-h1-full">Policies</h1>
            <div className="prose prose-mt">
              <p>
                {branding.name} has not published its privacy policy or terms of
                service here yet.
              </p>
              {(branding.email || branding.phone) && (
                <p>
                  For questions about your data or your service, contact{' '}
                  {branding.email && (
                    <a href={`mailto:${branding.email}`}>{branding.email}</a>
                  )}
                  {branding.email && branding.phone && ' or '}
                  {branding.phone && branding.phoneHref && (
                    <a href={branding.phoneHref}>{branding.phone}</a>
                  )}
                  .
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="section-sm">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Legal</p>
          <h1 className="display-md page-h1-full">Policies</h1>
          <p className="lede chapter-lede">Plain language. No surprises.</p>

          <div className="prose prose-mt">

            <h2>Privacy Policy</h2>
            <p>Effective date: December 2025</p>

            <h3>What we collect</h3>
            <p>
              When you request a quote, book a service, or contact us, we collect your name,
              email address, phone number, and service address. Payment is processed through
              Stripe &mdash; we never see or store your card number.
            </p>

            <h3>How we use it</h3>
            <p>
              We use your information to schedule and deliver cleaning services, communicate
              about appointments, and send post-visit receipts and quality surveys. We do not
              sell your data to third parties.
            </p>

            <h3>Cookies and analytics</h3>
            <p>
              Our website uses session cookies for authentication. We also use Google
              Analytics and Google Ads conversion tracking to measure which marketing
              campaigns bring people to us &mdash; these cookies record anonymized
              browsing behavior on this site only. You can opt out of personalized
              advertising at{' '}
              <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">
                adssettings.google.com
              </a>{' '}or disable cookies in your browser.
            </p>

            <h3>Lead forms and how we contact you</h3>
            <p>
              When you submit a form on this site, you may be asked to consent to a
              call or text from us at the number you provide. We only use your phone
              number to respond to your inquiry and to provide service updates.
              Consent is not required to receive a quote &mdash; you can also reach us
              directly by phone or email. Message and data rates may apply. To opt
              out of further messages, reply STOP to any text or email us at{' '}
              <a href="mailto:questions@nlcstpete.com">questions@nlcstpete.com</a>.
            </p>

            <h3>Retention</h3>
            <p>
              We keep your data while you are an active client and for two years after your
              last service. To request deletion, email{' '}
              <a href="mailto:questions@nlcstpete.com">questions@nlcstpete.com</a>.
            </p>

            <h2>Terms of Service</h2>

            <h3>Payment</h3>
            <p>
              Services are priced per square foot as published on our pricing page. Payment
              is charged after each completed visit. No deposit is required. Prices do not
              change without 30 days written notice.
            </p>

            <h3>Cancellation</h3>
            <p>
              Cancel any visit with 24 hours&rsquo; notice &mdash; no fee. Cancellations
              with less than 24 hours&rsquo; notice are charged 50% of the scheduled visit.
              There is no cancellation fee for ending recurring service.
            </p>

            <h3>What we do not cover</h3>
            <ul>
              <li>Walls (move-out cleans: walls are the painter&rsquo;s job)</li>
              <li>Exterior surfaces, windows, or gutters</li>
              <li>Hazardous materials or biohazards</li>
              <li>Damage caused by pre-existing conditions</li>
            </ul>

            <h3>Equipment</h3>
            <p>
              We bring all cleaning supplies and equipment. If you ask us to use yours,
              we are not liable for damage to it.
            </p>

            <h3>Claims</h3>
            <p>
              Any claim for damage or loss must be filed within 48 hours of the visit.
              Liability is capped at the cost of that visit.
            </p>

            <h3>Governing law</h3>
            <p>These terms are governed by the laws of the State of Florida.</p>

            <h3>Questions</h3>
            <p>
              Contact us at{' '}
              <a href="mailto:questions@nlcstpete.com">questions@nlcstpete.com</a>.
            </p>

          </div>
        </div>
      </section>
    </main>
  );
}
